import React from 'react';
import { observer } from 'mobx-react';
import { BackButton } from 'ui/base/button/back_button';
import { PublicPage } from 'ui/base/page/public_page/public_page';
import { Theme, ThemedStyles, useStyles } from 'ui/base/theme';
import { ActiveButton } from 'ui/public/button';
import { LabeledInput, TextInput } from 'ui/public/input';
import { PasswordResetStore } from './password_reset_store';
import PasswordResetMessages from './password_reset_messages';
import baseStyles from './password_reset.module.css';
import { FormSubmitEvent } from 'ui/base/types';
import { ErrorMessage } from 'ui/public/error_message/error_message';
import { useHistory } from 'react-router-dom';
import { ResultKind } from 'services/base/types';
import { BasePath, pagePathToPathString } from 'services/base/page_path';

type PasswordResetStyles = {
  title: string;
  buttonContainer: string;
};

const themedStyles: ThemedStyles<PasswordResetStyles> = {
  [Theme.DAYLIGHT]: {
    title: baseStyles.daylightTitle,
    buttonContainer: baseStyles.buttonContainer,
  },
  [Theme.MIDNIGHT]: {
    title: baseStyles.midnightTitle,
    buttonContainer: baseStyles.buttonContainer,
  },
};

const getRedirectPath = (email: string): string => {
  const path = pagePathToPathString({
    kind: BasePath.PASSWORD_RESET_CONFIRMATION,
  });
  return `${path}?email=${encodeURI(email)}`;
};

type BasePasswordResetProps = {
  store: PasswordResetStore;
};

export const BasePasswordReset = observer(({ store }: BasePasswordResetProps) => {
  const history = useHistory();
  const handlePasswordReset = React.useCallback(async(event: FormSubmitEvent) => {
    event.preventDefault();
    const result = await store.busy.perform(() => store.handlePasswordReset());
    if (result.kind === ResultKind.OK) {
      history.push(getRedirectPath(store.email.value));
    }
  }, [history, store]);
  const styles = useStyles(themedStyles);
  return (
    <PublicPage>
      <BackButton/>
      <form onSubmit={handlePasswordReset}>
        <div className={styles.title}>
          {PasswordResetMessages.passwordReset()}
        </div>
        <LabeledInput label={PasswordResetMessages.email()}>
          <TextInput store={store.email} id='email-address' name='email-address'/>
        </LabeledInput>
        <ErrorMessage message={store.emailError}/>
        <div className={styles.buttonContainer}>
          <ActiveButton busy={store.busy} type='submit'>{PasswordResetMessages.submit()}</ActiveButton>
        </div>
      </form>
    </PublicPage>
  );
});

export const PasswordReset = () => {
  const [store] = React.useState(new PasswordResetStore());
  return <BasePasswordReset store={store}/>;
};